import { Fragment } from "react";
import { motion } from "framer-motion";
import LazyLoadingImg from "../Components/LazyLoadingImg";
import useWindowWidth from "../../hooks/useWindowWidth";

// Reusable motion wrapper
const MotionSwitch = ({
  isMobile,
  as = "div",
  className,
  children,
  initial,
  inView,
  transition,
  ...rest
}) => {
  const Tag = as;
  if (!isMobile) {
    return (
      <Tag className={className} {...rest}>
        {children}
      </Tag>
    );
  }
  const MotionTag = motion[as] ?? motion.div;
  return (
    <MotionTag
      className={className}
      initial={initial}
      whileInView={inView}
      viewport={{ once: true, amount: 0.5 }}
      transition={transition ?? { duration: 0.6, ease: "easeOut" }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
};

export default function HighlightedFeatures() {
  const width = useWindowWidth();
  const isMobile = width <= 600;

  return (
    <Fragment>
      <div className="container-fluid horizontal-padding pb-5">
        {/* Title */}
        <div className="px-lg-6 a2Features text-center position-relative">
          <MotionSwitch
            isMobile={isMobile}
            as="h2"
            initial={{ opacity: 0, y: -40 }}
            inView={{ opacity: 1, y: 0 }}
          >
            HIGHLIGHTED FEATURES
          </MotionSwitch>
        </div>

        {/* Section 1 */}
        <div className="row my-md-5 my-3 mx-0 a2-features-box-shadow">
          <MotionSwitch
            isMobile={isMobile}
            className="col-lg-6 col-md-6 align-content-center"
            initial={{ opacity: 0, x: -50 }}
            inView={{ opacity: 1, x: 0 }}
          >
            <LazyLoadingImg
              src="/images/a2-features-pic-1.png"
              alt="a2-portfolio-overview"
              className="img-fluid"
            />
          </MotionSwitch>
          <MotionSwitch
            isMobile={isMobile}
            className="col-lg-6 col-md-6 position-relative"
            initial={{ opacity: 0, x: 50 }}
            inView={{ opacity: 1, x: 0 }}
          >
            <h3 className="a2-font-features-5">Campaign and Automation</h3>
            {[
              {
                title: "Schedule Mail",
                text: "Plan and set the timing for email distribution.",
              },
              {
                title: "Decide Frequency",
                text: "Choose the frequency for sending out campaign emails.",
              },
              {
                title: "Automate Mail Sending",
                text: "Implement automation for streamlined and timely email dispatch in campaigns.",
              },
            ].map((item, i) => (
              <MotionSwitch
                key={i}
                isMobile={isMobile}
                className="row a2-feature-box py-3 mt-3 mx-0"
                initial={{ opacity: 0, y: 30 }}
                inView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="col-1 text-end">
                  <img src="/images/a2-check.png" alt="a2-check" />
                </div>
                <div className="col-11">
                  <div className="a2-font-features-6">{item.title}</div>
                  <div className="a2-font-features-7">{item.text}</div>
                </div>
              </MotionSwitch>
            ))}
          </MotionSwitch>
        </div>

        {/* Section 2 */}
        <div className="row my-md-5 my-3 mx-0 a2-features-box-shadow column-reverse">
          <MotionSwitch
            isMobile={isMobile}
            className="col-lg-6 col-md-6 position-relative align-content-center"
            initial={{ opacity: 0, x: -50 }}
            inView={{ opacity: 1, x: 0 }}
          >
            <h3 className="a2-font-features-5">Customized Queries</h3>
            <p className="a2-font-features-7">
              Locate contacts with specific characteristics by applying custom
              filters, generate a list based on the criteria, and organize them
              accordingly for efficient data management.
            </p>
            <MotionSwitch
              isMobile={isMobile}
              className="row a2-feature-box py-3 mt-3 mx-0"
              initial={{ opacity: 0, y: 30 }}
              inView={{ opacity: 1, y: 0 }}
            >
              <div className="col-1 text-end">
                <img src="/images/a2-check.png" alt="a2-check" />
              </div>
              <div className="col-11">
                <div className="a2-font-features-6">Search Bar</div>
                <div className="a2-font-features-7">
                  Users can search for any contact, details, account, email, or
                  phone number.
                </div>
              </div>
            </MotionSwitch>
          </MotionSwitch>
          <MotionSwitch
            isMobile={isMobile}
            className="col-lg-6 col-md-6 align-content-center"
            initial={{ opacity: 0, x: 50 }}
            inView={{ opacity: 1, x: 0 }}
          >
            <LazyLoadingImg
              src="/images/a2-features-pic-2.png"
              alt="a2-portfolio-overview"
              className="img-fluid"
            />
          </MotionSwitch>
        </div>

        {/* Section 3 */}
        <div className="row my-md-5 my-3 mx-0 a2-features-box-shadow">
          <MotionSwitch
            isMobile={isMobile}
            className="col-lg-6 col-md-6 align-content-center"
            initial={{ opacity: 0, x: -50 }}
            inView={{ opacity: 1, x: 0 }}
          >
            <LazyLoadingImg
              src="/images/a2-features-pic-3.png"
              alt="a2-portfolio-overview"
              className="img-fluid"
            />
          </MotionSwitch>
          <MotionSwitch
            isMobile={isMobile}
            className="col-lg-6 col-md-6 position-relative"
            initial={{ opacity: 0, x: 50 }}
            inView={{ opacity: 1, x: 0 }}
          >
            <h3 className="a2-font-features-5">Virtual Admin</h3>
            {[
              {
                title: "Custom Fields",
                text: "View all custom field data across features.",
              },
              {
                title: "Daily Task List",
                text: "Assign and view tasks on a daily basis, accessible account-wise.",
              },
              {
                title: "Import Contacts and Data",
                text: "Save mappings for efficient data import.",
              },
            ].map((item, i) => (
              <MotionSwitch
                key={i}
                isMobile={isMobile}
                className="row a2-feature-box py-3 mt-3 mx-0"
                initial={{ opacity: 0, y: 30 }}
                inView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="col-1 text-end">
                  <img src="/images/a2-check.png" alt="a2-check" />
                </div>
                <div className="col-11">
                  <div className="a2-font-features-6">{item.title}</div>
                  <div className="a2-font-features-7">{item.text}</div>
                </div>
              </MotionSwitch>
            ))}
          </MotionSwitch>
        </div>

        {/* Section 4 */}
        <div className="row mt-md-5 mt-3 mx-0 a2-features-box-shadow column-reverse">
          <MotionSwitch
            isMobile={isMobile}
            className="col-lg-6 col-md-6 position-relative"
            initial={{ opacity: 0, x: -50 }}
            inView={{ opacity: 1, x: 0 }}
          >
            <h3 className="a2-font-features-5">
              Marketing Dashboard and Statistics
            </h3>
            {[
              {
                title: "Template Editor",
                text: "Easily customize templates for various purposes.",
              },
              {
                title: "Preview",
                text: "Review a preview of the template before finalizing.",
              },
              {
                title: "Mail Merge Fields",
                text: "Personalize content by using mail merge fields.",
              },
              {
                title: "Advanced Editor",
                text: "Access advanced editing options for complex customization.",
              },
              {
                title: "Share Template",
                text: "Collaborate efficiently by sharing templates with others.",
              },
              {
                title: "Statistics",
                text: "The dashboard provides detailed graphs of tasks and activity statistics.",
              },
            ].map((item, i) => (
              <MotionSwitch
                key={i}
                isMobile={isMobile}
                className="row a2-feature-box py-3 mt-3 mx-0"
                initial={{ opacity: 0, y: 30 }}
                inView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="col-1 text-end">
                  <img src="/images/a2-check.png" alt="a2-check" />
                </div>
                <div className="col-11">
                  <div className="a2-font-features-6">{item.title}</div>
                  <div className="a2-font-features-7">{item.text}</div>
                </div>
              </MotionSwitch>
            ))}
          </MotionSwitch>
          <MotionSwitch
            isMobile={isMobile}
            className="col-lg-6 col-md-6 align-content-center"
            initial={{ opacity: 0, x: 50 }}
            inView={{ opacity: 1, x: 0 }}
          >
            <LazyLoadingImg
              src="/images/a2-features-pic-4.png"
              alt="a2-portfolio-overview"
              className="img-fluid"
            />
          </MotionSwitch>
        </div>
      </div>
    </Fragment>
  );
}
